// dashboardFilterBus.js
// Per-dashboard cross-filter pub/sub. Panels emit filters when their data
// points are clicked; other panels subscribe and merge those filters into
// their own WHERE clauses. Slicer panels and drill-through will publish to
// the same bus in later phases.
//
// Filter shape:
//   { sourceId: string,  column: string,  value: string|number,  operator?: '=' }
//
// A dashboard renders inside <DashboardFilterProvider>. Panels call
// useCrossFilters() to read the current active set + emit new ones.

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const Ctx = createContext(null);

export function DashboardFilterProvider({ children }) {
  // Map<sourceId, { column, value, operator }>. One filter per source panel.
  // Clicking a new point on the same source panel replaces its filter;
  // clicking the same value again clears it.
  const [filters, setFilters] = useState({});

  const emit = useCallback((sourceId, column, value, operator = '=') => {
    setFilters(prev => {
      const cur = prev[sourceId];
      // toggle-off if user clicked the exact same point
      if (cur && cur.column === column && String(cur.value) === String(value)) {
        const { [sourceId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [sourceId]: { column, value, operator } };
    });
  }, []);

  const clearOne = useCallback((sourceId) => {
    setFilters(prev => {
      if (!prev[sourceId]) return prev;
      const { [sourceId]: _, ...rest } = prev;
      return rest;
    });
  }, []);

  const clearAll = useCallback(() => setFilters({}), []);

  // Bulk-replace the whole filter map. Used to restore bookmarks and to seed
  // filters from URL params on drill-through arrival.
  const restore = useCallback((snapshot) => {
    if (!snapshot) { setFilters({}); return; }
    if (Array.isArray(snapshot)) {
      const map = {};
      snapshot.forEach((f, i) => {
        if (f && f.column && f.value !== undefined) {
          map[f.sourceId || `restored_${i}`] = {
            column: f.column, value: f.value, operator: f.operator || '='
          };
        }
      });
      setFilters(map);
    } else if (typeof snapshot === 'object') {
      setFilters({ ...snapshot });
    } else {
      setFilters({});
    }
  }, []);

  // What each panel sees: the array of filters NOT emitted by itself. This
  // prevents a panel from filtering itself into a one-row island when the
  // user clicks a bar in that same panel.
  const filtersFor = useCallback((panelId) => {
    return Object.entries(filters)
      .filter(([src]) => src !== panelId)
      .map(([, f]) => f);
  }, [filters]);

  const value = useMemo(() => ({
    filters, emit, clearOne, clearAll, restore, filtersFor
  }), [filters, emit, clearOne, clearAll, restore, filtersFor]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useCrossFilters() {
  const ctx = useContext(Ctx);
  // If a panel is rendered outside a provider (e.g. focus mode or standalone
  // preview), degrade gracefully — nothing to emit or subscribe to.
  if (!ctx) {
    return {
      filters: {},
      emit: () => {},
      clearOne: () => {},
      clearAll: () => {},
      restore: () => {},
      filtersFor: () => []
    };
  }
  return ctx;
}
